import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import logo from "@/assets/logo.png";

interface HeroProps {
  isRTL: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

interface UpcomingEvent {
  id: string;
  image_url: string | null;
  title: string;
  title_ar: string | null;
  category: string | null;
  date: string | null;
}

export const Hero = ({ isRTL }: HeroProps) => {
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  // Auto-rotate featured image
  useEffect(() => {
    if (upcomingEvents.length > 0) {
      const interval = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % upcomingEvents.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [upcomingEvents.length]);

  const fetchUpcomingEvents = async () => {
    const { data, error } = await supabase.
    from("scraped_events").
    select("id, image_url, title, title_ar, category, date").
    eq("is_published", true).
    not("image_url", "is", null).
    order("scraped_at", { ascending: false }).
    limit(20);

    if (!error && data) {
      const uniqueByCategory: UpcomingEvent[] = [];
      const seenCategories = new Set<string>();

      for (const event of data) {
        if (event.image_url && uniqueByCategory.length < 6) {
          const cat = event.category || "general";
          if (!seenCategories.has(cat) || uniqueByCategory.length < 4) {
            seenCategories.add(cat);
            uniqueByCategory.push(event);
          }
        }
      }

      if (uniqueByCategory.length < 6) {
        for (const event of data) {
          if (event.image_url && !uniqueByCategory.find((e) => e.id === event.id)) {
            uniqueByCategory.push(event);
            if (uniqueByCategory.length >= 6) break;
          }
        }
      }

      setUpcomingEvents(uniqueByCategory);
    }
  };

  const displayImages = upcomingEvents.
  filter((event) => event.image_url).
  map((event) => ({
    url: event.image_url!,
    title: isRTL ? event.title_ar || event.title : event.title
  }));

  return (
    <section
      aria-label={isRTL ? "قسم الترحيب الرئيسي" : "Hero section"}
      className="relative min-h-[550px] sm:min-h-[750px] overflow-hidden text-white"
    >
      {/* Gradient Background - matching Footer/Navigation */}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-[#1a0f22] via-[#241530] to-[#1a0f22]" />
      
      {/* Decorative Pattern - matching Footer */}
      <div aria-hidden="true" className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="heroPattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M30 0L60 30L30 60L0 30Z" fill="none" stroke="white" strokeWidth="0.5" />
              <circle cx="30" cy="30" r="10" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#heroPattern)" />
        </svg>
      </div>

      {/* Glowing Orbs - matching Footer */}
      <div aria-hidden="true" className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
      <div aria-hidden="true" className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
      <div aria-hidden="true" className="absolute top-1/2 right-1/3 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl" />

      {/* Animated background particles */}
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden motion-reduce:hidden">
        {[...Array(20)].map((_, i) =>
        <motion.div
          key={i}
          className="absolute w-2 h-2 bg-primary/30 rounded-full"
          initial={{
            x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
            y: Math.random() * 750
          }}
          animate={{
            y: [null, -100],
            opacity: [0, 1, 0]
          }}
          transition={{
            duration: 5 + Math.random() * 5,
            repeat: Infinity,
            delay: Math.random() * 5
          }} />

        )}
      </div>

      {/* Decorative mandala pattern - top left with rotation animation */}
      <motion.div
        aria-hidden="true"
        className="absolute -top-10 -left-10 w-60 h-60 opacity-10 motion-reduce:hidden"
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}>
        
        <svg viewBox="0 0 100 100" className="w-full h-full text-primary">
          <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="0.3" />
          <circle cx="50" cy="50" r="38" fill="none" stroke="currentColor" strokeWidth="0.3" />
          <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="0.3" />
          <circle cx="50" cy="50" r="22" fill="none" stroke="currentColor" strokeWidth="0.3" />
          <circle cx="50" cy="50" r="14" fill="none" stroke="currentColor" strokeWidth="0.3" />
          {[...Array(24)].map((_, i) =>
          <line key={i} x1="50" y1="5" x2="50" y2="95" stroke="currentColor" strokeWidth="0.2" transform={`rotate(${i * 15} 50 50)`} />
          )}
        </svg>
      </motion.div>

      {/* Decorative mandala pattern - bottom right with counter rotation */}
      <motion.div
        aria-hidden="true"
        className="absolute -bottom-20 -right-20 w-80 h-80 opacity-10 motion-reduce:hidden"
        animate={{ rotate: -360 }}
        transition={{ duration: 80, repeat: Infinity, ease: "linear" }}>
        
        <svg viewBox="0 0 100 100" className="w-full h-full text-primary">
          <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="0.3" />
          <circle cx="50" cy="50" r="35" fill="none" stroke="currentColor" strokeWidth="0.3" />
          <circle cx="50" cy="50" r="25" fill="none" stroke="currentColor" strokeWidth="0.3" />
          {[...Array(16)].map((_, i) =>
          <line key={i} x1="50" y1="5" x2="50" y2="95" stroke="currentColor" strokeWidth="0.2" transform={`rotate(${i * 22.5} 50 50)`} />
          )}
        </svg>
      </motion.div>

      {/* Decorative wave - top right with shimmer effect */}
      <motion.div
        aria-hidden="true"
        className="absolute top-0 right-0 w-64 h-48"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, delay: 0.5 }}>
        
        <svg viewBox="0 0 200 150" className="w-full h-full">
          <defs>
            <linearGradient id="primaryGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" />
              <stop offset="50%" stopColor="hsl(152, 100%, 40%)" />
              <stop offset="100%" stopColor="hsl(var(--primary))" />
            </linearGradient>
          </defs>
          <motion.path
            d="M200,0 Q140,70 200,140 L200,0 Z"
            fill="url(#primaryGradient)"
            opacity="0.3"
            animate={{ opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 3, repeat: Infinity }} />
          
          <path d="M170,0 Q110,50 170,100 L200,100 L200,0 Z" fill="hsl(var(--primary))" opacity="0.15" />
        </svg>
      </motion.div>

      {/* Animated bottom border */}
      <div aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-emerald-500 to-primary" />

      <div className="container relative z-10 py-6 sm:py-12">
        {/* Header section with entrance animation */}
        <motion.div
          className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-10"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}>
          
          <div className="bg-white rounded-2xl p-1.5 shadow-lg">
            <motion.img
              src={logo}
              alt="دليل يدل - Yadel"
              className="w-16 h-16 sm:w-28 sm:h-28 object-contain"
              animate={{
                rotateY: [0, 10, 0, -10, 0]
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />
            
          </div>
          <div>
            <motion.p
              className="text-sm tracking-widest italic text-white/80"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}>
              
              {isRTL ? "اكتشف أحدث" : "DISCOVER THE"}
            </motion.p>
            <motion.p
              className="text-2xl font-bold tracking-wide bg-gradient-to-r from-primary-300 to-primary-500 bg-clip-text text-transparent"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}>
              
              {isRTL ? "الفعاليات" : "LATEST EVENTS"}
            </motion.p>
          </div>
        </motion.div>

        {/* Main heading with staggered animation */}
        <motion.h1
          className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 sm:mb-12 text-center max-w-4xl mx-auto leading-tight"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}>
          
          <span className="bg-gradient-to-r from-white via-gold/90 to-white/80 bg-clip-text text-transparent text-4xl">
            {isRTL ?
            "جميع الفعاليات والمؤتمرات في المملكة العربية السعودية" :
            "All events and conferences in Saudi Arabia"
            }
          </span>
        </motion.h1>

        {/* Featured event showcase */}
        {displayImages.length > 0 &&
        <motion.div
          className="relative mb-8"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}>
          
            {/* Main featured image */}
            <div className="relative max-w-2xl mx-auto h-64 md:h-80 rounded-2xl overflow-hidden shadow-2xl">
              <AnimatePresence mode="wait">
                <motion.div
                key={activeIndex}
                className="absolute inset-0"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.7 }}>
                
                  <img
                  src={displayImages[activeIndex]?.url}
                  alt={displayImages[activeIndex]?.title}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-full object-cover" />
                
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <motion.div
                  className="absolute bottom-0 left-0 right-0 p-6"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}>
                  
                    <p className="text-white text-lg md:text-xl font-semibold line-clamp-2">
                      {displayImages[activeIndex]?.title}
                    </p>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
              
              {/* Navigation dots */}
              <div
                role="tablist"
                aria-label={isRTL ? "اختر الفعالية المعروضة" : "Select featured event"}
                className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10"
              >
                {displayImages.map((_, idx) =>
              <button
                key={idx}
                role="tab"
                aria-selected={idx === activeIndex}
                aria-label={
                  isRTL
                    ? `عرض الفعالية ${idx + 1} من ${displayImages.length}`
                    : `Show featured event ${idx + 1} of ${displayImages.length}`
                }
                onClick={() => setActiveIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40 ${
                idx === activeIndex ?
                'bg-primary w-6' :
                'bg-white/70 hover:bg-white'}`
                } />

              )}
              </div>
            </div>
          </motion.div>
        }

        {/* Floating image thumbnails */}
        <div className="flex flex-wrap justify-center items-center gap-4 mb-10">
          {displayImages.slice(0, 5).map((image, index) => {
            const delays = [0.6, 0.7, 0.8, 0.9, 1.0];
            const rotations = [-6, 4, -3, 5, -4];
            const sizes = [
            "w-32 h-24 md:w-40 md:h-32",
            "w-36 h-28 md:w-44 md:h-36",
            "w-32 h-24 md:w-40 md:h-32",
            "hidden md:block w-36 h-28",
            "hidden lg:block w-32 h-24"];


            return (
              <motion.div
                key={index}
                role="button"
                tabIndex={0}
                aria-label={
                  isRTL
                    ? `عرض الفعالية: ${image.title}`
                    : `Show event: ${image.title}`
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActiveIndex(index);
                  }
                }}
                className={`${sizes[index]} rounded-xl overflow-hidden shadow-lg cursor-pointer border-2 border-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a0f22]`}
                initial={{ opacity: 0, y: 50, rotate: rotations[index] }}
                animate={{
                  opacity: 1,
                  y: 0,
                  rotate: rotations[index]
                }}
                whileHover={{
                  scale: 1.1,
                  rotate: 0,
                  zIndex: 10,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.3)"
                }}
                transition={{
                  duration: 0.5,
                  delay: delays[index],
                  type: "spring",
                  stiffness: 200
                }}
                onClick={() => setActiveIndex(index)}>
                
                <img
                  src={image.url}
                  alt={image.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                
              </motion.div>);

          })}
        </div>

        {/* CTA Button with pulse animation */}
        <motion.div
          className="flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}>
          
          <Link to="/imported-events">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}>
              
              <Button
                className="relative bg-gradient-to-r from-secondary to-gold border-2 border-gold/30 text-white hover:from-secondary/90 hover:to-gold/90 px-10 py-7 text-lg font-semibold rounded-full transition-all duration-300 overflow-hidden group shadow-lg shadow-gold/20">
                
                <motion.span
                  className="absolute inset-0 bg-primary/20"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }} />
                
                <span className="relative z-10">
                  {isRTL ? "عرض جميع الفعاليات" : "VIEW ALL EVENTS"}
                </span>
              </Button>
            </motion.div>
          </Link>
        </motion.div>
      </div>
    </section>);

};